import { useMemo, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import { fleetApi, type Cost, type RunJob, type Ups, type WaitingJob } from '../lib/fleet-api.js';
import { jobUrl } from '../lib/fleet-runs.js';
import { fleetToken } from '../lib/fleet-token.js';
import { jobFromQuery, jobPath } from '../lib/fleet-jobs.js';
import { RunHistory } from './RunHistory.js';
import { JobAnalysis } from './JobAnalysis.js';
import { CiRouting } from './CiRouting.js';
import {
  heatmap,
  livenessView,
  minutes,
  seconds,
  share,
  tickView,
  usd,
} from '../lib/fleet-view.js';
import { durationText, inFlight } from '../lib/fleet-boxes.js';
import { watts } from '../lib/fleet-ups.js';
import { useAsync } from '../lib/useAsync.js';
import { usePoll } from '../lib/usePoll.js';
import { count, numeric } from '../lib/format.js';
import { AsyncBoundary, Badge, DotPill, EmptyState, Note, Panel, Table, Td, When } from '../components/ui.js';
import { InfoBubble, Menu, MenuItem } from '../components/Screen.js';
import { TabNav, resolveTab } from '../components/Record.js';
import type { FleetAlertsState } from '../lib/useFleetAlerts.js';
import { AlertBanner, BoxCards } from './CiBoxes.js';
import { UpsCards } from './CiUps.js';
import { JobDetails } from './JobDetails.js';

/**
 * CI fleet: the six self-hosted runners, what is waiting for them, how long
 * runs take, how busy the fleet gets, and what it saves. org-admin#96.
 *
 * ── A DIFFERENT BACKEND FROM THE REST OF THE PORTAL ───────────────────
 *
 * Every other screen reads the auth service's admin API with the session
 * cookie. This one reads fleet-api with a bearer token for fleet-api's own
 * audience; see lib/fleet-api.ts and lib/fleet-token.ts. The numbers are
 * computed there, so every figure here can be reproduced from its jobs table.
 *
 * ── SECTIONS ARE PATH SEGMENTS ───────────────────────────────────────
 *
 * The same pattern as System health (org-admin#57): an incident link can point
 * at "the queue" rather than at "the fleet screen, then a tab".
 */
const SECTIONS = [
  { path: 'now', label: 'Runners and queue' },
  // Where CI runs, and moving it by hand (org-admin#98).
  { path: 'routing', label: 'Routing' },
  // The path stays `history` so existing links keep working; the label is what
  // the tab is called now (org-admin#111).
  { path: 'history', label: 'Run history' },
  { path: 'jobs', label: 'Job analysis' },
  { path: 'capacity', label: 'Capacity' },
  { path: 'cost', label: 'Cost' },
] as const;

type Section = (typeof SECTIONS)[number]['path'];

/** Exported for its test: an unrecognised segment lands on the first section. */
export function resolve(section?: string): Section {
  return resolveTab(SECTIONS, section);
}

const api = fleetApi(fleetToken);

/** Runner kinds, in the order and colours every panel here uses. */
const KINDS = [
  { kind: 'fleet', label: 'Your runners', color: C.brand },
  { kind: 'blacksmith', label: 'Blacksmith', color: C.violet },
  { kind: 'github', label: 'GitHub-hosted', color: C.turquoise },
] as const;
const kindLabel = (kind: string) => KINDS.find((k) => k.kind === kind)?.label ?? kind;

export function CiFleet({
  section,
  query,
  navigate,
  alerts = null,
}: {
  section?: string;
  query: Record<string, string>;
  navigate: (to: string) => void;
  /** From the app shell, which also shows their count on the nav (org-admin#97). */
  alerts?: FleetAlertsState | null;
}) {
  const current = resolve(section);
  return (
    <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {/* Above the tabs, so an alert is seen from whichever tab a link opened. */}
      <AlertBanner state={alerts} />
      <TabNav base="/ci-fleet" tabs={SECTIONS} current={current} label="CI fleet sections" />
      {current === 'history' ? (
        <HistorySection key="history" navigate={navigate} />
      ) : current === 'jobs' ? (
        <JobAnalysis
          key="jobs"
          job={jobFromQuery(query)}
          onChoose={(job) => navigate(jobPath(job))}
          loadSteps={api.steps}
          loadJobs={api.jobs}
        />
      ) : current === 'routing' ? (
        <CiRouting key="routing" load={api.routing} setLabel={api.setRouting} />
      ) : current === 'capacity' ? (
        <CapacitySection key="capacity" />
      ) : current === 'cost' ? (
        <CostSection key="cost" />
      ) : (
        <NowSection key="now" navigate={navigate} />
      )}
    </div>
  );
}

// ── RUNNERS AND QUEUE ────────────────────────────────────────────────

/** What the queue knows about a waiting job that the details dialog does not. */
const waitingRows = (j: WaitingJob): [string, ReactNode][] => [
  ['State', j.stuck ? <Badge key="s" tone="danger">Stuck</Badge> : <Badge key="s">Queued</Badge>],
  ['Waiting', seconds(j.waiting_s)],
  ['Matching runners', j.matching_runners.length > 0 ? j.matching_runners.map((m) => `${m.name}${m.idle ? ' (idle)' : ''}`).join(', ') : 'None'],
];

/** The running job on the runner board, as a control that opens its details. */
const jobButton = {
  font: 'inherit',
  color: C.brand,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  textAlign: 'left' as const,
};

function NowSection({ navigate }: { navigate: (to: string) => void }) {
  const runners = useAsync(() => api.runners());
  const queue = useAsync(() => api.queue());
  const status = useAsync(() => api.status());
  const boxes = useAsync(() => api.boxes());
  /**
   * The job whose details are open (org-admin#126), with the rows only the panel
   * it came from knows. Its GitHub link is where a stuck job is re-run.
   */
  const [details, setDetails] = useState<{ job: RunJob; extra: [string, ReactNode][] } | null>(null);
  usePoll(runners.reload);
  usePoll(queue.reload);
  usePoll(status.reload);
  // A box reports once a minute, so an action queued or running is picked up or
  // finished within one. Read the boxes more often only while one is, so its
  // result shows soon after it arrives rather than up to a minute later.
  const actionPending = boxes.state.status === 'ready' && boxes.state.data.boxes.some((b) => inFlight(b) !== null);
  usePoll(boxes.reload, actionPending ? 10_000 : 60_000);

  // The status endpoint ADDS to the runner board rather than gating it: the
  // board still renders if only this request fails, and says so underneath.
  const fleetStatus = status.state.status === 'ready' ? status.state.data : null;
  const tick = fleetStatus ? tickView(fleetStatus.collector) : null;

  return (
    <div style={{ display: 'grid', gap: '1rem' }} className="rw-fade">
      {status.state.status === 'failed' ? (
        <Note tone="warn">Could not read the collector's status, so running jobs are not shown. {status.state.message}</Note>
      ) : null}
      {fleetStatus?.backfill.running ? (
        <Note>
          Collecting history back to {fleetStatus.backfill.until ?? 'the start'}. Run times and capacity fill in as it goes.
        </Note>
      ) : null}
      {/* The UPS behind every box (fleet-api#59), from the same answer as the box cards. */}
      {boxes.state.status === 'ready' && (boxes.state.data.ups ?? []).length > 0 ? (
        <Panel
          title="UPS"
          info={
            <InfoBubble label="About the UPS">
              <p style={{ margin: 0 }}>
                One UPS backs headless-1, headless-2, headless-3 and the Mac mini. headless-2 has its USB cable and reads it
                once a minute. When the power goes, every box shuts down at the line shown, and starts again when the power is
                back. Load is in whole percent of the UPS's rating, so watts move in 10 W steps.
              </p>
            </InfoBubble>
          }
        >
          <UpsCards ups={boxes.state.data.ups ?? []} />
        </Panel>
      ) : null}
      <AsyncBoundary state={boxes.state}>
        {(d) => (
          <Panel
            title="Boxes"
            info={
              <InfoBubble label="About box health">
                <p style={{ margin: 0 }}>
                  Each box reports its own health once a minute. A box is <strong>silent</strong> after{' '}
                  {durationText(d.silent_after_s)} without a report, and its readings are then its last ones. A runner's
                  connection comes from the box itself, so it shows beside what GitHub says rather than instead of it.
                </p>
              </InfoBubble>
            }
          >
            <BoxCards
              boxes={d.boxes}
              runners={runners.state.status === 'ready' ? runners.state.data.runners : null}
              running={fleetStatus ? fleetStatus.running : null}
              requestAction={api.requestBoxAction}
              cancelAction={api.cancelBoxAction}
              onChanged={() => boxes.reload(true)}
            />
          </Panel>
        )}
      </AsyncBoundary>
      <AsyncBoundary state={runners.state}>
        {(d) => (
          <Panel
            title="Runners"
            action={tick ? <DotPill tone={tick.tone}>{tick.label}</DotPill> : undefined}
            info={
              <InfoBubble label="About runner status">
                <p style={{ margin: 0 }}>
                  GitHub reports these runners as offline while they work (actions/runner#3892), so the status here
                  comes from what each runner has actually done. <strong>Connected, GitHub reports offline</strong> is
                  the normal state, not a fault.
                </p>
              </InfoBubble>
            }
          >
            {d.runners.length === 0 ? (
              <EmptyState title="No runners registered" suggestion="The collector records runners from the ridgewill-ci runner group." />
            ) : (
              <Table columns={['Runner', 'Status', 'Running now', 'Last job', 'Last seen']}>
                {d.runners.map((r) => {
                  const view = livenessView(r.liveness);
                  const job = fleetStatus?.running.find((j) => j.runner_name === r.name);
                  return (
                    <tr key={r.name}>
                      <Td mono>{r.name}</Td>
                      <Td>
                        <DotPill tone={view.tone}>{view.label}</DotPill>
                      </Td>
                      <Td>
                        {job ? (
                          <button
                            type="button"
                            onClick={() => setDetails({ job, extra: [['Running for', seconds(job.elapsed_s)]] })}
                            title={`${job.repo} · ${job.workflow} · ${job.job_name}. Show details.`}
                            style={jobButton}
                            className="rw-press"
                          >
                            {job.repo} · {job.job_name}{' '}
                            <span style={{ ...numeric, color: C.muted }}>{seconds(job.elapsed_s)}</span>
                          </button>
                        ) : fleetStatus ? (
                          // Not "Idle": a runner with no job may equally be down,
                          // and the Status column is where that is judged.
                          <span style={{ color: C.muted }}>No job</span>
                        ) : null}
                      </Td>
                      <Td>{r.last_job_at ? <When iso={r.last_job_at} /> : 'Never'}</Td>
                      <Td>{r.last_seen_at ? <When iso={r.last_seen_at} /> : '—'}</Td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Panel>
        )}
      </AsyncBoundary>

      <AsyncBoundary state={queue.state}>
        {(d) => {
          const ours = d.jobs.filter((j) => j.for_our_runners);
          const stuck = ours.filter((j) => j.stuck).length;
          return (
            <Panel
              title="Waiting for a runner"
              action={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.75rem' }}>
                  <DotPill tone={stuck > 0 ? 'danger' : ours.length > 0 ? 'warn' : 'good'}>
                    {stuck > 0 ? `${count(stuck)} stuck` : ours.length > 0 ? `${count(ours.length)} waiting` : 'None waiting'}
                  </DotPill>
                  <span style={{ fontSize: '.8rem', color: C.muted, whiteSpace: 'nowrap' }}>
                    {d.last_tick_at ? (
                      <>
                        Updated <When iso={d.last_tick_at} />
                      </>
                    ) : (
                      'Not collected yet'
                    )}
                  </span>
                </span>
              }
            >
              {ours.length === 0 ? (
                <EmptyState title="No jobs are waiting for your runners" />
              ) : (
                <Table columns={['Job', 'Waiting', 'Matching runners', 'State', '']} actionsLast>
                  {ours.map((j) => (
                    // Double-click or the row's menu, as in Run history's list.
                    <tr
                      key={j.id}
                      className="rw-rowhover"
                      onDoubleClick={(ev) => {
                        if ((ev.target as HTMLElement).closest('button, a')) return;
                        setDetails({ job: j, extra: waitingRows(j) });
                      }}
                    >
                      <Td>
                        {j.repo} · {j.workflow} · {j.job_name}
                      </Td>
                      <Td>
                        <span style={numeric}>{seconds(j.waiting_s)}</span>
                      </Td>
                      <Td>
                        {j.matching_runners.map((m) => `${m.name}${m.idle ? ' (idle)' : ''}`).join(', ')}
                      </Td>
                      <Td>{j.stuck ? <Badge tone="danger">Stuck</Badge> : <Badge>Queued</Badge>}</Td>
                      <Td>
                        <Menu label={`Options for ${j.workflow} · ${j.job_name}`}>
                          {(close) => (
                            <>
                              <MenuItem
                                onClick={() => {
                                  close();
                                  setDetails({ job: j, extra: waitingRows(j) });
                                }}
                              >
                                Show details
                              </MenuItem>
                              <MenuItem href={jobUrl(j)} external onClick={close}>
                                Open on GitHub
                              </MenuItem>
                            </>
                          )}
                        </Menu>
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}
            </Panel>
          );
        }}
      </AsyncBoundary>

      {details ? (
        <JobDetails job={details.job} extra={details.extra} onClose={() => setDetails(null)} onAnalyse={(job) => navigate(jobPath(job))} />
      ) : null}
    </div>
  );
}

// ── RUN TIMES ────────────────────────────────────────────────────────

/**
 * The run-history chart and list (org-admin#111). The step breakdown and box
 * comparison that sat under it are the Job analysis tab now (JobAnalysis.tsx);
 * a job's details dialog links there.
 */
function HistorySection({ navigate }: { navigate: (to: string) => void }) {
  return (
    <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'minmax(0, 1fr)' }} className="rw-fade">
      <RunHistory load={api.runs} onAnalyse={(job) => navigate(jobPath(job))} />
    </div>
  );
}

// ── CAPACITY ─────────────────────────────────────────────────────────

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function CapacitySection() {
  const { state } = useAsync(() => api.capacity(30));
  return (
    <AsyncBoundary state={state}>
      {(d) => {
        const grid = heatmap(d.hours);
        return (
          <div style={{ display: 'grid', gap: '1rem' }} className="rw-fade">
            <Panel title="Is a fourth box worth building?">
              <div style={s.tiles}>
                <Tile label="Queue wait, median" value={seconds(d.queue.p50_s)} note={`${count(d.queue.jobs)} jobs in 30 days`} />
                <Tile label="Queue wait, p95" value={seconds(d.queue.p95_s)} note={`Longest ${seconds(d.queue.max_s)}`} />
                <Tile label="Any runner busy" value={share(d.concurrency.busy_share)} note="Share of the window" />
                <Tile label={`All ${count(d.runners)} busy`} value={share(d.concurrency.all_busy_share)} note={`Peak ${count(d.concurrency.peak)} at once`} />
              </div>
            </Panel>
            <Panel title="When the fleet is busy" info={<InfoBubble label="About the heatmap"><p style={{ margin: 0 }}>Busy runner-time by weekday and hour, in your time zone, over the last 30 days. Darker is busier.</p></InfoBubble>}>
              <div style={{ padding: '1rem 1.2rem', overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: '.72rem', color: C.muted }}>
                  <tbody>
                    {grid.cells.map((row, i) => (
                      <tr key={WEEKDAYS[i]}>
                        <th scope="row" style={{ fontWeight: 600, textAlign: 'left', paddingRight: '.4rem' }}>
                          {WEEKDAYS[i]}
                        </th>
                        {row.map((v, h) => (
                          <td
                            key={h}
                            title={`${WEEKDAYS[i]} ${String(h).padStart(2, '0')}:00, ${seconds(v)} busy`}
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 3,
                              background: v > 0 ? C.brand : C.page,
                              opacity: v > 0 && grid.max > 0 ? Math.max(0.15, v / grid.max) : 1,
                              border: v > 0 ? 'none' : `1px solid ${C.border}`,
                            }}
                          />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        );
      }}
    </AsyncBoundary>
  );
}

// ── COST ─────────────────────────────────────────────────────────────

function CostSection() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const { state } = useAsync(() => api.cost(month), [month]);
  // The UPS's measured load, shown beside the estimate (fleet-api#59). A failed
  // read costs that one tile, not the panel.
  const boxes = useAsync(() => api.boxes());
  const ups = boxes.state.status === 'ready' ? (boxes.state.data.ups ?? []).find((u) => u.state === 'reporting') ?? null : null;
  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) =>
      new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)).toISOString().slice(0, 7),
    );
  }, []);

  return (
    <AsyncBoundary state={state}>
      {(d) => <CostPanels cost={d} month={month} months={months} onMonth={setMonth} ups={ups} />}
    </AsyncBoundary>
  );
}

function CostPanels({
  cost,
  month,
  months,
  onMonth,
  ups,
}: {
  cost: Cost;
  month: string;
  months: string[];
  onMonth: (m: string) => void;
  /** The UPS, when one is reporting: its measured load goes beside the estimate. */
  ups: Ups | null;
}) {
  const partial = cost.elapsed_share < 1;
  const used = Math.min(1, cost.blacksmith.used_min / Math.max(1, cost.blacksmith.free_min));
  return (
    <div style={{ display: 'grid', gap: '1rem' }} className="rw-fade">
      <Panel
        title={partial ? `${month}, projected` : month}
        action={
          <Select
            size="sm"
            align="end"
            label="Which month"
            value={month}
            onChange={onMonth}
            options={months.map((m) => ({ value: m, label: m }))}
          />
        }
      >
        <div style={s.tiles}>
          <Tile label="Saved against Blacksmith" value={usd(partial ? cost.avoided.projected : cost.avoided.to_date)} note={partial ? `${usd(cost.avoided.to_date)} so far` : 'For the month'} />
          <Tile label="Electricity, estimated" value={usd(partial ? cost.electricity.projected : cost.electricity.to_date)} note={`${count(cost.assumptions.boxes)} boxes at ${cost.assumptions.avg_watts_per_box} W`} />
          <Tile label="Net" value={usd(partial ? cost.net.projected : cost.net.to_date)} note={partial ? `${usd(cost.net.to_date)} so far` : 'For the month'} />
          {ups && ups.last.watts !== null ? (
            <Tile label="Measured at the UPS, now" value={watts(ups.last.watts)} note="The three CI boxes and the Mac mini, in 10 W steps" />
          ) : null}
        </div>
        <div style={{ padding: '0 1.2rem 1rem', display: 'grid', gap: '.35rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem', color: C.muted }}>
            <span>Blacksmith free minutes</span>
            <span style={numeric}>
              {count(cost.blacksmith.used_min)} of {count(cost.blacksmith.free_min)}
              {partial ? `, ${count(cost.blacksmith.projected_min)} projected` : ''}
            </span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: C.page, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(used * 100)}%`, height: '100%', background: cost.blacksmith.projected_min > cost.blacksmith.trip_min ? C.warnDot : C.successDot }} />
          </div>
        </div>
        <p style={s.footnote}>
          The saving is small, and the screen should say so: the real gain is capacity and a bill that cannot creep. Saved is
          what your runners' minutes would have cost on Blacksmith past its free {count(cost.blacksmith.free_min)}, at{' '}
          {usd(cost.assumptions.blacksmith_usd_per_min)} a minute. Electricity is an estimate at{' '}
          {usd(cost.assumptions.electricity_usd_per_kwh)} per kWh.
          {/* fleet-api#59: the UPS reading is not used for the estimate. It reads in
              10 W steps of a 1000 W rating, so idle boxes read 0 W, and it includes
              the Mac mini, which is not a CI box. */}
          {ups && ups.last.watts !== null
            ? ' The UPS figure is shown beside it rather than used for it: it moves in 10 W steps, reads 0 W while the boxes idle, and includes the Mac mini.'
            : ''}
          {/* The projection is what has happened plus the rest of the month at the
              recent pace, not the month's average (fleet-api cost.ts). Said, because
              "projected" alone reads as a straight line. */}
          {partial && cost.projection
            ? ` Projections add the rest of the month at the pace of the last ${count(Math.round(cost.projection.days))} day${Math.round(cost.projection.days) === 1 ? '' : 's'}.`
            : ''}
        </p>
      </Panel>

      <Panel title="Minutes by runner type">
        <Table columns={['Runner type', 'Run time', 'Billed']}>
          {cost.minutes.map((m) => (
            <tr key={m.kind}>
              <Td>{kindLabel(m.kind)}</Td>
              <Td>
                <span style={numeric}>{minutes(m.run_min)}</span>
              </Td>
              <Td>
                <span style={numeric}>{m.kind === 'fleet' ? `${minutes(cost.fleet.billed_equivalent_min)} if on Blacksmith` : minutes(m.billable_min)}</span>
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>
      {cost.github_hosted.source === 'jobs' ? (
        <Note>GitHub-hosted minutes are counted from collected jobs, because fleet-api has not read GitHub's bill for {month}.</Note>
      ) : null}
    </div>
  );
}

function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div style={s.tile}>
      <span style={s.tileLabel}>{label}</span>
      <span style={{ ...s.tileValue, ...numeric }}>{value}</span>
      <span style={s.tileNote}>{note}</span>
    </div>
  );
}

const s = {
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: '.75rem',
    padding: '1rem 1.1rem',
  },
  tile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '.15rem',
    padding: '.7rem .85rem .75rem',
    background: C.page,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
  },
  tileLabel: { fontSize: '.75rem', fontWeight: 600, color: C.muted },
  tileValue: { fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.2 },
  tileNote: { fontSize: '.75rem', color: C.muted },
  footnote: { margin: 0, padding: '0 1.1rem 1.1rem', fontSize: '.82rem', lineHeight: 1.55, color: C.muted },
};
